/** Extracts the 11-character video ID from any common YouTube address (or a bare ID). Returns null if none. */
export function parseYoutubeId(input: string): string | null {
  const raw = (input ?? "").trim();
  if (!raw) return null;
  if (/^[\w-]{11}$/.test(raw)) return raw;
  let u: URL;
  try { u = new URL(/^[a-z]+:\/\//i.test(raw) ? raw : "https://" + raw); } catch { return null; }
  const host = u.hostname.toLowerCase().replace(/^(www|m|music)\./, "");
  const id = (s: string | undefined) => (s && /^[\w-]{11}$/.test(s) ? s : null);
  if (host === "youtu.be") return id(u.pathname.split("/")[1]);
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    const v = u.searchParams.get("v");
    if (v) return id(v);
    const m = u.pathname.match(/^\/(?:shorts|embed|live|v|e)\/([\w-]{11})/);
    if (m) return m[1];
  }
  return null;
}

export interface ThumbSize { file: string; label: string; w: number; h: number }
export const THUMB_SIZES: ThumbSize[] = [
  { file: "maxresdefault", label: "Max resolution", w: 1280, h: 720 },
  { file: "sddefault", label: "Standard", w: 640, h: 480 },
  { file: "hqdefault", label: "High quality", w: 480, h: 360 },
  { file: "mqdefault", label: "Medium", w: 320, h: 180 },
  { file: "default", label: "Small", w: 120, h: 90 },
];

export const thumbUrl = (id: string, file: string) => `https://img.youtube.com/vi/${id}/${file}.jpg`;

/** YouTube answers a missing size with a small grey placeholder instead of an error. */
export const isPlaceholder = (naturalWidth: number, expectedW: number) => naturalWidth <= 120 && expectedW > 120;

/**
 * Every real video has the High quality (480x360) and Medium (320x180) thumbnails. If both come back as
 * YouTube's 120x90 grey placeholder (or fail to load), the video ID does not exist or has no thumbnail.
 */
export const isVideoNotFound = (missing: Record<string, boolean>) => !!missing.hqdefault && !!missing.mqdefault;

/** The sizes YouTube really has for this video (the ones not found to be missing). */
export const availableSizes = (missing: Record<string, boolean>) => THUMB_SIZES.filter(t => !missing[t.file]);

/** Name of one size's file, the same in the single downloads and inside the ZIP. */
export const thumbFileName = (id: string, file: string) => `youtube-${id}-${file}.jpg`;
export const thumbZipName = (id: string) => `youtube-${id}-thumbnails.zip`;

/**
 * The size to preview: the largest one that exists. Sizes are tried largest first; one that is still loading
 * holds the choice (so the preview never flashes a smaller size first), one found missing is skipped.
 */
export function previewSize(missing: Record<string, boolean>, loaded: Record<string, { w: number; h: number }>): ThumbSize | "loading" | null {
  for (const t of THUMB_SIZES) {
    if (missing[t.file]) continue;
    return loaded[t.file] ? t : "loading";
  }
  return null;
}

/** True once every size has either loaded or been found missing. */
export const allSettled = (missing: Record<string, boolean>, loaded: Record<string, unknown>) => THUMB_SIZES.every(t => missing[t.file] || loaded[t.file]);

/**
 * Fetches each size and bundles them into one ZIP, in the browser. A size that can't be fetched is listed in `failed`
 * and left out; if none can be fetched (a browser that blocks reading the images) it throws, so the caller can say so
 * and point to the single downloads.
 */
export async function buildThumbZip(id: string, sizes: ThumbSize[], fetchImpl: (url: string) => Promise<Response> = (u) => fetch(u)): Promise<{ blob: Blob; included: string[]; failed: string[] }> {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  const included: string[] = [];
  const failed: string[] = [];
  await Promise.all(sizes.map(async t => {
    try {
      const res = await fetchImpl(thumbUrl(id, t.file));
      if (!res.ok) throw new Error(String(res.status));
      zip.file(thumbFileName(id, t.file), await res.arrayBuffer());
      included.push(t.file);
    } catch { failed.push(t.file); }
  }));
  if (included.length === 0) throw new Error("no thumbnail could be fetched");
  return { blob: await zip.generateAsync({ type: "blob" }), included: THUMB_SIZES.filter(t => included.includes(t.file)).map(t => t.file), failed: THUMB_SIZES.filter(t => failed.includes(t.file)).map(t => t.file) };
}
